import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import { Shield, Plus, AlertCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { fetchRemediationTemplate, getScoreLevelFromScore } from "@/api/fetchRemediationTemplate";
import type { DbRemediationTemplate } from "@/api/fetchRemediationTemplate";
import { fetchValidTemplateControls } from "@/api/fetchValidTemplateControls";
import { FindingEntry, hasEntryData, isEntryComplete } from "@/components/reviewer/FindingEntry";
import type { FindingData } from "@/components/reviewer/FindingEntry";

export type { FindingData };

export interface FindingBuilderRef {
  getData: () => FindingData[];
  hasData: () => boolean;
  isComplete: () => boolean;
}

interface FindingBuilderProps {
  reviewerScore: number | null;
  controls: string[];
  existingFindings?: Partial<FindingData>[] | null;
  disabled?: boolean;
  required?: boolean;
  hidden?: boolean;
  questionnaireKey?: string;
  onFindingsChange?: (findings: FindingData[]) => void;
  /** Optional question key — only used for temporary multi-control debug logging. */
  questionKey?: string;
}


const FindingBuilder = forwardRef<FindingBuilderRef, FindingBuilderProps>(
  ({ reviewerScore, controls, existingFindings, disabled = false, required = false, hidden = false, questionnaireKey = "telecom_v2", onFindingsChange, questionKey }, ref) => {
    const [expanded, setExpanded] = useState(false);
    const [findingsMap, setFindingsMap] = useState<Record<string, FindingData>>({});
    const [addControlValue, setAddControlValue] = useState("");
    const [validControls, setValidControls] = useState<Set<string> | null>(null);

    // Phase 4 guardrail: only allow selection of controls that have pre-filled language
    useEffect(() => {
      let cancelled = false;
      fetchValidTemplateControls(questionnaireKey).then(set => {
        if (!cancelled) setValidControls(set);
      });
      return () => { cancelled = true; };
    }, [questionnaireKey]);

    // Populate from existing findings
    useEffect(() => {
      if (existingFindings && existingFindings.length > 0) {
        const map: Record<string, FindingData> = {};
        for (const f of existingFindings) {
          if (f.control_code) {
            map[f.control_code] = {
              finding_title: f.finding_title ?? "",
              priority_level: f.priority_level ?? "",
              recommended_action: f.recommended_action ?? "",
              timeframe: f.timeframe ?? "",
              recommended_measures: f.recommended_measures ?? "",
              remediation_path: f.remediation_path ?? "",
              risk_level: f.risk_level ?? "",
              risk_override: f.risk_override ?? false,
              control_code: f.control_code,
              owners: Array.isArray((f as any).owners) ? (f as any).owners : [],
              due_date: (f as any).due_date ?? null,
            };
          }
        }
        setFindingsMap(map);
        if (Object.keys(map).length > 0) setExpanded(true);
      }
    }, [existingFindings]);

    // Fetch remediation template and return a pre-filled FindingData
    const fetchAndBuildFinding = useCallback(async (ctrl: string): Promise<FindingData> => {
      const empty: FindingData = {
        finding_title: "",
        priority_level: "",
        recommended_action: "",
        timeframe: "",
        recommended_measures: "",
        remediation_path: "",
        risk_level: "",
        risk_override: false,
        control_code: ctrl,
        owners: [],
        due_date: null,
      };
      try {
        const tpl = await fetchRemediationTemplate(ctrl, reviewerScore, questionnaireKey);
        if (tpl) {
          return {
            finding_title: tpl.finding_title ?? "",
            priority_level: tpl.priority_level ?? "",
            recommended_action: tpl.recommended_action ?? "",
            timeframe: tpl.timeframe ?? "",
            recommended_measures: tpl.recommended_measures ?? "",
            remediation_path: tpl.remediation_path ?? "",
            risk_level: tpl.risk_level ?? "",
            risk_override: false,
            control_code: ctrl,
            owners: [],
            due_date: null,
          };
        }
      } catch (e) {
        console.warn("[FindingBuilder] Failed to fetch remediation template", e);
      }
      return empty;
    }, [reviewerScore, questionnaireKey]);

    // Auto-expand and auto-create first finding when required.
    // Wait for validControls so we pick a control that has pre-filled language.
    useEffect(() => {
      if (required && controls.length > 0 && Object.keys(findingsMap).length === 0 && validControls) {
        const firstValid = controls.find(c => validControls.has(c)) ?? controls[0];
        if (!firstValid) return;
        setExpanded(true);
        fetchAndBuildFinding(firstValid).then(finding => {
          setFindingsMap(prev => {
            if (Object.keys(prev).length > 0) return prev;
            return { [firstValid]: finding };
          });
        });
      }
    }, [required, controls, validControls]);

    // Auto-expand when multi-control and the score requires findings, so the
    // secondary "+ Add Finding" affordance is immediately discoverable.
    useEffect(() => {
      if (required && controls.length > 1) {
        setExpanded(true);
      }
    }, [required, controls.length]);

    const controlsWithFindings = Object.keys(findingsMap);
    // Phase 4 guardrail: hide controls without pre-filled language from the dropdown.
    // While loading (validControls === null), allow all to avoid flashing empty state.
    // If the runtime template-control list is incomplete for a multi-control question,
    // keep the question metadata as authoritative so the secondary finding area is not hidden.
    const templateFilteredControls = validControls
      ? controls.filter(c => validControls.has(c))
      : controls;
    const selectableControls = validControls && controls.length > 1 && templateFilteredControls.length < controls.length
      ? controls
      : templateFilteredControls;
    const availableControls = selectableControls.filter(c => !findingsMap[c]);

    // TEMP DEBUG: log GV-04 and multi-control questions to diagnose Production visibility issue.
    // Remove after Production validation. GV-04 logs even if runtime controls unexpectedly collapse to 1.
    useEffect(() => {
      if (questionKey === "GV-04" || controls.length > 1) {
        // eslint-disable-next-line no-console
        console.debug("[FindingBuilder multi-control debug]", {
          questionKey,
          questionnaireKey,
          controls,
          validTemplateControls: validControls ? Array.from(validControls) : null,
          templateFilteredControls,
          selectableControls,
          findingsMap: Object.keys(findingsMap),
          availableControls,
          reviewerScore,
          required,
          hidden,
          isExpanded: expanded,
        });
      }
    }, [questionKey, questionnaireKey, controls, validControls, templateFilteredControls, selectableControls, availableControls, findingsMap, reviewerScore, required, hidden, expanded]);


    const handleAddFinding = async () => {
      const ctrl = addControlValue || availableControls[0];
      if (!ctrl || findingsMap[ctrl]) return;
      setAddControlValue("");
      // Fetch template FIRST so the entry mounts already populated.
      // FindingEntry initializes its local state from `existingFinding` exactly
      // once (initialized.current guard), so inserting an empty entry and then
      // patching findingsMap would leave the entry permanently blank until it
      // remounted (e.g. via a score toggle to Implemented and back).
      const populated = await fetchAndBuildFinding(ctrl);
      setFindingsMap(prev => {
        if (prev[ctrl]) return prev; // race guard
        return { ...prev, [ctrl]: populated };
      });
    };

    const handleDeleteFinding = (controlCode: string) => {
      setFindingsMap(prev => {
        const next = { ...prev };
        delete next[controlCode];
        return next;
      });
    };

    const handleFindingChange = (controlCode: string, data: FindingData) => {
      setFindingsMap(prev => ({ ...prev, [controlCode]: data }));
    };

    const getData = useCallback((): FindingData[] => {
      return Object.values(findingsMap).filter(f => hasEntryData(f));
    }, [findingsMap]);

    const hasData = useCallback((): boolean => {
      return Object.values(findingsMap).some(f => hasEntryData(f));
    }, [findingsMap]);

    const isComplete = useCallback((): boolean => {
      const entries = Object.values(findingsMap);
      if (entries.length === 0) return !required;
      return entries.every(f => isEntryComplete(f));
    }, [findingsMap, required]);

    useImperativeHandle(ref, () => ({ getData, hasData, isComplete }), [getData, hasData, isComplete]);

    useEffect(() => {
      onFindingsChange?.(getData());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [findingsMap, getData]);

    if (hidden) return null;

    const findingCount = controlsWithFindings.length;

    return (
      <div className="mt-4 rounded-lg border border-border">
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="flex items-center justify-between w-full px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors rounded-t-lg"
        >
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" />
            <span>Findings</span>
            {required && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                Required
              </Badge>
            )}
            {selectableControls.length > 1 ? (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {findingCount} of {selectableControls.length} finding areas added
              </Badge>
            ) : findingCount > 0 ? (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {findingCount}
              </Badge>
            ) : null}
          </div>
          <span className="text-xs text-muted-foreground">
            {expanded ? "Collapse" : "Expand"}
          </span>
        </button>

        {expanded && (
          <div className="px-4 pb-4 space-y-4 border-t border-border pt-4">
            {controlsWithFindings.map(cc => (
              <FindingEntry
                key={cc}
                controlCode={cc}
                reviewerScore={reviewerScore}
                existingFinding={findingsMap[cc]}
                disabled={disabled}
                onDelete={() => handleDeleteFinding(cc)}
                onChange={(data) => handleFindingChange(cc, data)}
                defaultExpanded={controlsWithFindings.length === 1}
              />
            ))}

            {availableControls.length > 0 && !disabled && (
              <div className="pt-2">
                {availableControls.length === 1 ? (
                  <button
                    type="button"
                    onClick={() => {
                      setAddControlValue(availableControls[0]);
                      handleAddFinding();
                    }}
                    className="inline-flex items-center justify-center gap-1.5 w-full text-sm text-primary hover:text-primary hover:bg-primary/5 font-medium border-2 border-dashed border-primary/40 hover:border-primary rounded-md px-4 py-2 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add Finding for {availableControls[0]}
                  </button>
                ) : (
                  <div className="flex items-center gap-2 border-2 border-dashed border-primary/40 hover:border-primary rounded-md px-3 py-2 transition-colors">
                    <Select value={addControlValue} onValueChange={setAddControlValue}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Select control" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableControls.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={handleAddFinding}
                      disabled={!addControlValue}
                      className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Plus className="h-4 w-4" />
                      Add Finding
                    </button>
                  </div>
                )}
              </div>
            )}

            {findingCount === 0 && !disabled && (
              <div className="text-center py-3">
                <p className="text-sm text-muted-foreground">
                  {required ? (
                    <span className="flex items-center justify-center gap-1.5 text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      At least one finding is required for this score
                    </span>
                  ) : (
                    "No findings created yet"
                  )}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

FindingBuilder.displayName = "FindingBuilder";
export { FindingBuilder };
